import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import { SEARCH_TRADEMARK_CODE_ARRAY } from "_constants";
import breakpoints from "_constants/breakpoints";

import { ordersActions } from "_store/actions";

import GeneralInfo from "components/OrderDetail/GeneralInfo";
import CommonLawResults from "components/OrderDetail/Results/CommonLawResults/CommonLawResults";
import GlobalSearchResults from "components/OrderDetail/Results/GlobalSearchResults/GlobalSearchResults";
import StateSearchResults from "components/OrderDetail/Results/StateSearchResults/StateSearchResults";
import FederalSearchResults from "components/OrderDetail/Results/FederalSearchResults/FederalSearchResults";

import { ErrorModal } from "components/Modals/ErrorModal";
import { SpinnerWrapper } from "widget/Spinner";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 500px;

  animation: 0.2s linear fadeIn;
  @keyframes fadeIn {
    from {
      opacity: 0%;
    }
    to {
      opacity: 100%;
    }
  }
`;
const Title = styled.h2`
  margin-bottom: 14px;

  font-weight: bold;
  font-size: 24px;
  line-height: 47px;
  color: #03589c;

  @media (max-width: ${breakpoints.mobileM}) {
    font-size: 18px;
    margin-left: 0px;
  }
`;
const Column = styled.div`
  max-width: 1364px;
  width: 100%;
  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      padding: 0 20px;

      @media (max-width: ${breakpoints.mobileM}) {
        padding: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    `}
`;
const Button = styled.button`
  width: 209px;
  height: 42px;
  margin-bottom: 24px;
  background: #f37217;
  border: none;
  border-radius: 4px;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  color: #fafaff;

  @media (max-width: ${breakpoints.mobileM}) {
    width: 90%;
  }
`;

export default function TrademarkSearch() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orderDetails, error, orderDetailsLoading } = useSelector(({ orders }) => orders);

  const { orderId } = useParams();

  useEffect(() => {
    dispatch(ordersActions.getOrder(orderId));
  }, [orderId, dispatch]);

  if (!orderDetails)
    return (
      <Container key="container-trademarksearch-alert">
        {(orderDetailsLoading && <SpinnerWrapper />) || (error && <ErrorModal />)}
      </Container>
    );

  const searchDetails = orderDetails.products.find(({ code }) => SEARCH_TRADEMARK_CODE_ARRAY.includes(code.code));

  if (!searchDetails) {
    navigate(`/orders/${orderId}`);

    return null;
  }

  const isCommonLawProduct = searchDetails.code.code === "TRADEMARK_Trademark_Search_Common_Law";
  const isGlobalProduct = searchDetails.code.code === "TRADEMARK_Trademark_Search_Global";
  const isBasicProduct = searchDetails.code.code === "TRADEMARK_Trademark_Search_Basic";

  return (
    <Container key="container-trademarksearch-main">
      <GeneralInfo orderDetails={orderDetails} />
      <Title>Search results</Title>
      <Column>
        <FederalSearchResults federalSearchResults={searchDetails.processingResult.federalSearchResults} />
        {!isBasicProduct && (
          <StateSearchResults stateSearchResults={searchDetails.processingResult.stateSearchResults} />
        )}
        {(isCommonLawProduct || isGlobalProduct) && (
          <CommonLawResults commonLawResults={searchDetails.processingResult.commonLawResults} />
        )}
        {isGlobalProduct && (
          <GlobalSearchResults globalSearchResults={searchDetails.processingResult.globalSearchResults} />
        )}
      </Column>
      <Button onClick={() => navigate(`/orders/${orderId}`)}>Go Back</Button>
    </Container>
  );
}
